import React, { useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { useTopicFilters } from '../../../../contexts/TopicFiltersContext';

/**
 * MobileSearchInput component
 * 
 * Mobile search input overlay that uses TopicFiltersContext.
 * Must be used within a TopicFiltersProvider.
 * 
 * Usage:
 * <MobileSearchInput 
 *   isVisible={showSearch}
 *   onClose={handleCloseSearch}
 * />
 */
interface MobileSearchInputProps {
  isVisible: boolean;
  onClose: () => void;
  className?: string;
}

export const MobileSearchInput: React.FC<MobileSearchInputProps> = ({
  isVisible,
  onClose,
  className,
}) => {
  // Get search state from context - clean and simple!
  const { searchQuery, setSearchQuery } = useTopicFilters();

  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the input when it becomes visible
  useEffect(() => {
    if (isVisible && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isVisible]);

  // Clear search query
  const handleClearSearch = () => {
    setSearchQuery('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Update search query when input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Handle search submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        'absolute inset-0 bg-[var(--content-bg-color)] z-50 h-16 flex items-center px-4',
        className
      )}
    >
      <form onSubmit={handleSubmit} className="flex-1 flex items-center">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search size={16} className="text-blue-700" />
          </div>

          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search..."
            className="w-full py-1.5 pl-10 pr-8 border rounded-lg border-gray-300 bg-[var(--content-bg-color)] focus:outline-none focus:ring-0"
          />

          {searchQuery && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Remove the submit button as filtering happens automatically */}

        <button
          type="button"
          onClick={onClose}
          className="ml-2 px-3 py-2 text-gray-600 rounded-lg"
          aria-label="Cancel search"
        >
          Cancel
        </button>
      </form>
    </div>
  );
};
